import { useState } from 'react';
import type { PaymentMethod, BattlePhase, Language, PaymentStatusResponse } from '@vikings/types';
import { t } from '@vikings/game-data';
import './PixelModal.css';

interface StatsModalProps {
  name: string;
  month: string;
  coinsFirst?: number;
  coinsFinal?: number;
  coinsTax?: number;
  coinsSpent?: number;
  onClose: () => void;
  language?: Language;
}

/**
 * Player/friend statistics modal — pixel-art style.
 * Displays:
 * - coins_first (здобуто в першій битві)
 * - coins_final (здобуто в фінальній битві)
 * - coins_tax (витрачено на зброю)
 */
export function StatsModal({
  name,
  month,
  coinsFirst = 0,
  coinsFinal = 0,
  coinsTax = 0,
  coinsSpent,
  onClose,
  language = 'uk',
}: StatsModalProps) {
  const tax = coinsTax || coinsSpent || 0;

  return (
    <div className="pixel-overlay" onClick={onClose}>
      <div className="pixel-modal pixel-modal-stats" onClick={(e) => e.stopPropagation()}>
        <div className="pixel-modal-title">{name.toUpperCase()}</div>
        <div className="pixel-modal-divider" />
        <div className="pixel-modal-label">{t(language, 'thisMonth')}</div>
        <div className="pixel-modal-sublabel">{month}</div>
        <div className="pixel-modal-spacer" />

        <div className="pixel-stat-block">
          <div className="pixel-stat-label">{t(language, 'coinsWonFirstBattle')}</div>
          <div className="pixel-stat-value">🪙 {coinsFirst.toLocaleString()}</div>
        </div>

        <div className="pixel-stat-block">
          <div className="pixel-stat-label">{t(language, 'coinsWonFinalBattle')}</div>
          <div className="pixel-stat-value">🪙 {coinsFinal.toLocaleString()}</div>
        </div>

        <div className="pixel-stat-block">
          <div className="pixel-stat-label">{t(language, 'coinsSpentWeapons')}</div>
          <div className="pixel-stat-value">🪙 {tax.toLocaleString()}</div>
        </div>

        <div className="pixel-modal-spacer" />
        <button className="pixel-button" onClick={onClose}>
          {t(language, 'ok')}
        </button>
      </div>
    </div>
  );
}

interface PaymentModalProps {
  payment?: PaymentStatusResponse;
  amount?: number;
  onSelectMethod: (method: PaymentMethod) => void;
  onClose: () => void;
  language?: Language;
}

/**
 * Payment / Sacrifice modal — two-step flow:
 * 1. Choice of sacrifice type with warning note.
 * 2. Details window for chosen type with amount from economy sheet, lore text, and confirmation/cancel buttons.
 */
export function PaymentModal({
  payment,
  amount,
  onSelectMethod,
  onClose,
  language = 'uk',
}: PaymentModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);

  if (!selectedMethod) {
    return (
      <div className="pixel-overlay" onClick={onClose}>
        <div className="pixel-modal pixel-modal-wide" onClick={(e) => e.stopPropagation()}>
          <div className="pixel-modal-title">{t(language, 'sacrificeChoiceTitle')}</div>
          <div className="pixel-modal-divider" />
          <div className="pixel-sacrifice-warning">
            ⚠️ {t(language, 'sacrificeWarningNote')}
          </div>
          <div className="pixel-modal-spacer" />
          <div className="pixel-button-group">
            <button
              className="pixel-button pixel-button-gold"
              onClick={() => setSelectedMethod('coins')}
            >
              {t(language, 'coins')}
            </button>
            <button
              className="pixel-button pixel-button-green"
              onClick={() => setSelectedMethod('cabbage')}
            >
              {t(language, 'cabbage')}
            </button>
            <button
              className="pixel-button pixel-button-yellow"
              onClick={() => setSelectedMethod('stars')}
            >
              {t(language, 'stars')}
            </button>
            <button
              className="pixel-button pixel-button-blue"
              onClick={() => setSelectedMethod('tokens')}
            >
              {t(language, 'tokens')}
            </button>
          </div>
          <div className="pixel-modal-spacer" />
          <button className="pixel-button pixel-button-secondary" onClick={onClose}>
            {t(language, 'cancel')}
          </button>
        </div>
      </div>
    );
  }

  const methodConfig: Record<
    PaymentMethod,
    {
      title: string;
      unit: string;
      amount: number;
      note: string;
      btnClass: string;
      color: string;
    }
  > = {
    coins: {
      title: t(language, 'coins'),
      unit: t(language, 'coins'),
      amount: payment?.coins_sac ?? amount ?? 0,
      note: t(language, 'coinsSacrificeNote'),
      btnClass: 'pixel-button-gold',
      color: '#ffd700',
    },
    cabbage: {
      title: t(language, 'cabbage'),
      unit: t(language, 'cabbage'),
      amount: payment?.cabbage_sac ?? 0,
      note: t(language, 'cabbageSacrificeNote'),
      btnClass: 'pixel-button-green',
      color: '#4caf50',
    },
    stars: {
      title: t(language, 'stars'),
      unit: t(language, 'stars'),
      amount: payment?.star_sac ?? 0,
      note: t(language, 'starsSacrificeNote'),
      btnClass: 'pixel-button-yellow',
      color: '#ffeb3b',
    },
    tokens: {
      title: t(language, 'tokens'),
      unit: t(language, 'tokens'),
      amount: payment?.tokens_sac ?? 0,
      note: t(language, 'tokensSacrificeNote'),
      btnClass: 'pixel-button-blue',
      color: '#42a5f5',
    },
    green_papers: {
      title: t(language, 'cabbage'),
      unit: t(language, 'cabbage'),
      amount: payment?.cabbage_sac ?? 0,
      note: t(language, 'cabbageSacrificeNote'),
      btnClass: 'pixel-button-green',
      color: '#4caf50',
    },
    star_papers: {
      title: t(language, 'stars'),
      unit: t(language, 'stars'),
      amount: payment?.star_sac ?? 0,
      note: t(language, 'starsSacrificeNote'),
      btnClass: 'pixel-button-yellow',
      color: '#ffeb3b',
    },
  };

  const current = methodConfig[selectedMethod];

  return (
    <div className="pixel-overlay" onClick={onClose}>
      <div className="pixel-modal pixel-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="pixel-modal-title">{current.title}</div>
        <div className="pixel-modal-divider" />
        <div className="pixel-modal-label">{t(language, 'amountToSacrifice')}</div>
        <div className="pixel-modal-value" style={{ color: current.color }}>
          {current.amount} {current.unit}
        </div>
        <div className="pixel-modal-spacer" />
        <div className="pixel-sacrifice-note">{current.note}</div>
        <div className="pixel-modal-spacer" />
        <div className="pixel-button-group">
          <button
            className={`pixel-button ${current.btnClass}`}
            onClick={() => onSelectMethod(selectedMethod)}
          >
            {t(language, 'sacrificeMadeBtn')}
          </button>
          <button
            className="pixel-button pixel-button-secondary"
            onClick={() => setSelectedMethod(null)}
          >
            ← {t(language, 'back')}
          </button>
          <button className="pixel-button" onClick={onClose}>
            {t(language, 'cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BattleScheduleModalProps {
  currentPhase?: BattlePhase;
  onClose: () => void;
  language?: Language;
}

/**
 * Battle Schedule Modal — displays monthly battle and sacrifice schedule.
 */
export function BattleScheduleModal({
  currentPhase,
  onClose,
  language = 'uk',
}: BattleScheduleModalProps) {
  const isSacrificeActive =
    currentPhase === 'sacrifice_pending' || currentPhase === 'sacrifice_processing';
  const isFirstBattleActive =
    currentPhase === 'waiting_first_battle' || currentPhase === 'first_battle_day';
  const isFinalBattleActive =
    currentPhase === 'waiting_final_battle' || currentPhase === 'final_battle_day';

  return (
    <div className="pixel-overlay" onClick={onClose}>
      <div className="pixel-modal pixel-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="pixel-modal-title">{t(language, 'battleSchedule')}</div>
        <div className="pixel-modal-divider" />

        <div className="pixel-schedule-list">
          {/* 22nd — First Battle */}
          <div className={`pixel-schedule-item ${isFirstBattleActive ? 'active' : ''}`}>
            <div className="pixel-schedule-info">
              <span className="pixel-schedule-date">{t(language, 'day22')}</span>
              <span className="pixel-schedule-desc">{t(language, 'firstBattle')}</span>
            </div>
            {isFirstBattleActive && (
              <span className="pixel-schedule-badge">{t(language, 'currentPhase')}</span>
            )}
          </div>

          {/* 7th — Final Battle */}
          <div className={`pixel-schedule-item ${isFinalBattleActive ? 'active' : ''}`}>
            <div className="pixel-schedule-info">
              <span className="pixel-schedule-date">{t(language, 'day7')}</span>
              <span className="pixel-schedule-desc">{t(language, 'finalBattle')}</span>
            </div>
            {isFinalBattleActive && (
              <span className="pixel-schedule-badge">{t(language, 'currentPhase')}</span>
            )}
          </div>

          {/* 8-12th — Sacrifice to the Gods */}
          <div className={`pixel-schedule-item ${isSacrificeActive ? 'active' : ''}`}>
            <div className="pixel-schedule-info">
              <span className="pixel-schedule-date">{t(language, 'days8to12')}</span>
              <span className="pixel-schedule-desc">{t(language, 'sacrificeTime')}</span>
            </div>
            {isSacrificeActive && (
              <span className="pixel-schedule-badge">{t(language, 'currentPhase')}</span>
            )}
          </div>
        </div>

        <div className="pixel-modal-spacer" />
        <button className="pixel-button" onClick={onClose}>
          {t(language, 'ok')}
        </button>
      </div>
    </div>
  );
}

interface InfoModalProps {
  onClose: () => void;
  onLogout?: () => void;
  language?: Language;
}

/**
 * Game Info & Help modal — player guide.
 */
export function InfoModal({ onClose, onLogout, language = 'uk' }: InfoModalProps) {
  return (
    <div className="pixel-overlay" onClick={onClose}>
      <div className="pixel-modal pixel-modal-info" onClick={(e) => e.stopPropagation()}>
        <div className="pixel-modal-title" style={{ textAlign: 'center' }}>
          ℹ️ {t(language, 'infoTitle')}
        </div>
        <div className="pixel-modal-divider" />

        <div className="pixel-info-intro">{t(language, 'infoIntro')}</div>

        <div className="pixel-info-list">
          <div className="pixel-info-item">
            <span className="pixel-info-icon">ℹ️</span>
            <div className="pixel-info-text">{t(language, 'infoHelpItem')}</div>
          </div>
          <div className="pixel-info-item">
            <span className="pixel-info-icon">🚪</span>
            <div className="pixel-info-text">{t(language, 'infoLogoutItem')}</div>
          </div>
          <div className="pixel-info-item">
            <span className="pixel-info-icon">🌐</span>
            <div className="pixel-info-text">{t(language, 'infoLangItem')}</div>
          </div>
          <div className="pixel-info-item">
            <span className="pixel-info-icon">⚔️</span>
            <div className="pixel-info-text">{t(language, 'infoTimerItem')}</div>
          </div>
          <div className="pixel-info-item">
            <span className="pixel-info-icon">🛡️</span>
            <div className="pixel-info-text">{t(language, 'infoHeroItem')}</div>
          </div>
          <div className="pixel-info-item">
            <span className="pixel-info-icon">🪙</span>
            <div className="pixel-info-text">{t(language, 'infoCoinItem')}</div>
          </div>
        </div>

        <div className="pixel-info-tip">
          <div className="pixel-info-tip-title">
            <span>⚡</span>
            <span>{t(language, 'infoTipHeader')}</span>
          </div>
          <div className="pixel-info-tip-text">{t(language, 'infoTipText')}</div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '14px' }}>
          <button className="pixel-button" onClick={onClose}>
            {t(language, 'ok')}
          </button>
          {onLogout && (
            <button className="pixel-button pixel-button-danger" onClick={onLogout}>
              {t(language, 'logoutBtn')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface LogoutModalProps {
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  language?: Language;
}

/**
 * Logout confirmation modal.
 */
export function LogoutModal({
  onConfirm,
  onCancel,
  isLoading = false,
  language = 'uk',
}: LogoutModalProps) {
  return (
    <div className="pixel-overlay" onClick={onCancel}>
      <div className="pixel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pixel-modal-title" style={{ textAlign: 'center' }}>
          🚪 {t(language, 'logoutTitle')}
        </div>
        <div className="pixel-modal-divider" />

        <div className="pixel-modal-text" style={{ textAlign: 'center' }}>
          {t(language, 'logoutConfirm')}
        </div>

        <div className="pixel-modal-spacer" />

        <div className="pixel-button-group">
          <button
            className="pixel-button pixel-button-danger"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? t(language, 'loading') : t(language, 'logoutBtn')}
          </button>
          <button
            className="pixel-button pixel-button-secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t(language, 'cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ErrorModalProps {
  message?: string;
  onRetry: () => void;
  language?: Language;
}

/**
 * Error screen — pixel-art styled.
 */
export function ErrorModal({ message, onRetry, language = 'uk' }: ErrorModalProps) {
  return (
    <div className="pixel-overlay">
      <div className="pixel-modal">
        <div className="pixel-modal-title">{t(language, 'ohNo')}</div>
        <div className="pixel-modal-divider" />
        <div className="pixel-modal-text">
          {message ?? t(language, 'connectionLost')}
        </div>
        <div className="pixel-modal-spacer" />
        <button className="pixel-button" onClick={onRetry}>
          {t(language, 'retry')}
        </button>
      </div>
    </div>
  );
}

interface UnauthorizedScreenProps {
  message?: string;
  language?: Language;
}

/**
 * Unauthorized screen — for users not in the users sheet.
 */
export function UnauthorizedScreen({ message, language = 'uk' }: UnauthorizedScreenProps) {
  return (
    <div className="pixel-overlay">
      <div className="pixel-modal">
        <div className="pixel-modal-title">⚔️ VIKINGS</div>
        <div className="pixel-modal-divider" />
        <div className="pixel-modal-text">
          {message ?? t(language, 'notViking')}
        </div>
      </div>
    </div>
  );
}

interface LoadingScreenProps {
  language?: Language;
}

/**
 * Loading screen — pixel-art styled.
 */
export function LoadingScreen({ language = 'uk' }: LoadingScreenProps) {
  return (
    <div className="pixel-overlay">
      <div className="pixel-loading">
        <div className="pixel-modal-title">⚔️ VIKINGS</div>
        <div className="pixel-loading-dots">
          <span className="pixel-dot" />
          <span className="pixel-dot" />
          <span className="pixel-dot" />
        </div>
        <div className="pixel-modal-text">{t(language, 'loading')}</div>
      </div>
    </div>
  );
}

export { LightningLoadingOverlay } from './LightningLoadingOverlay';
export { PasswordModal } from './PasswordModal';

